/**
 * Resource Container Service
 *
 * Handles CRUD operations for container resource management.
 * This is separate from the customer-facing container.service.ts
 */

import { apiClient } from './api';
import type {
  CreateContainerDTO,
  AssignContainerDTO,
  RetrieveContainerDTO,
  ReturnToYardDTO,
  MarkForMaintenanceDTO,
  ContainerFilterDTO,
} from '../schemas/containerSchema';

/**
 * Container resource entity
 */
export interface Container {
  id: string;
  providerId: string;
  containerNumber: string;
  size: '10Y' | '20Y' | '30Y' | '40Y';
  type: 'rolloff' | 'compactor' | 'stationary';
  status: 'available' | 'deployed' | 'in_transit' | 'maintenance' | 'retired';
  condition: 'excellent' | 'good' | 'fair' | 'poor' | 'needs_repair';
  location: {
    type: 'yard' | 'job_site' | 'in_transit';
    address: string;
  };
  assignment?: {
    jobId: string;
    jobAddress: string;
    deploymentDate: string;
  };
  purchaseDate?: string;
  lastMaintenanceDate?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Container list response
 */
export interface ContainerListResponse {
  containers: Container[];
  total: number;
  byStatus: {
    available: number;
    deployed: number;
    in_transit: number;
    maintenance: number;
    retired: number;
  };
  filters?: ContainerFilterDTO;
}

/**
 * Container detail response
 */
export interface ContainerDetailResponse {
  container: Container;
}

/**
 * Container summary statistics
 */
export interface ContainerSummary {
  total: number;
  byStatus: {
    available: number;
    deployed: number;
    in_transit: number;
    maintenance: number;
    retired: number;
  };
  bySize: {
    '10Y': number;
    '20Y': number;
    '30Y': number;
    '40Y': number;
  };
}

/**
 * Resource Container Service
 */
class ResourceContainerService {
  private baseUrl = '/provider/resources/containers';

  /**
   * List all containers with optional filters
   */
  async list(filters?: ContainerFilterDTO): Promise<ContainerListResponse> {
    const params = new URLSearchParams();

    if (filters?.size) {
      params.append('size', filters.size);
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.availableOnly !== undefined) {
      params.append('available_only', filters.availableOnly.toString());
    }
    if (filters?.yardId) {
      params.append('yard_id', filters.yardId);
    }

    const url = params.toString()
      ? `${this.baseUrl}?${params.toString()}`
      : this.baseUrl;

    return apiClient.get<ContainerListResponse>(url);
  }

  /**
   * Get container by ID
   */
  async getById(id: string): Promise<Container> {
    const response = await apiClient.get<ContainerDetailResponse>(
      `${this.baseUrl}/${id}`
    );
    return response.container;
  }

  /**
   * Get container summary statistics
   */
  async getSummary(): Promise<ContainerSummary> {
    return apiClient.get<ContainerSummary>(`${this.baseUrl}/summary`);
  }

  /**
   * Create a new container
   */
  async create(data: CreateContainerDTO): Promise<Container> {
    const response = await apiClient.post<ContainerDetailResponse>(
      this.baseUrl,
      data
    );
    return response.container;
  }

  /**
   * Update container details
   */
  async update(id: string, data: Partial<CreateContainerDTO>): Promise<Container> {
    const response = await apiClient.patch<ContainerDetailResponse>(
      `${this.baseUrl}/${id}`,
      data
    );
    return response.container;
  }

  /**
   * Assign container to a job
   */
  async assignToJob(id: string, data: AssignContainerDTO): Promise<Container> {
    const response = await apiClient.post<ContainerDetailResponse>(
      `${this.baseUrl}/${id}/assign`,
      data
    );
    return response.container;
  }

  /**
   * Retrieve container from job
   */
  async retrieveFromJob(id: string, data: RetrieveContainerDTO): Promise<Container> {
    const response = await apiClient.post<ContainerDetailResponse>(
      `${this.baseUrl}/${id}/retrieve`,
      data
    );
    return response.container;
  }

  /**
   * Return container to yard
   */
  async returnToYard(id: string, data: ReturnToYardDTO): Promise<Container> {
    const response = await apiClient.post<ContainerDetailResponse>(
      `${this.baseUrl}/${id}/return`,
      data
    );
    return response.container;
  }

  /**
   * Mark container for maintenance
   */
  async markForMaintenance(id: string, data: MarkForMaintenanceDTO): Promise<Container> {
    const response = await apiClient.post<ContainerDetailResponse>(
      `${this.baseUrl}/${id}/maintenance`,
      data
    );
    return response.container;
  }

  /**
   * Retire container (soft delete)
   */
  async retire(id: string): Promise<Container> {
    const response = await apiClient.post<ContainerDetailResponse>(
      `${this.baseUrl}/${id}/retire`
    );
    return response.container;
  }

  /**
   * Delete container (hard delete - admin only)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }
}

// Export singleton instance
export const resourceContainerService = new ResourceContainerService();
